import { cn } from "@/lib/utils";
import { ColumnResizeHandle } from "./ColumnResizeHandle";
import type { ReactNode } from "react";

type HeaderResizeProps =
  | {
      resizable: true;
      resizeAriaLabel: string;
      resizeValueNow: number;
      resizeValueMin: number;
      resizeValueMax: number;
      onResizeStart: (
        clientX: number,
        headerElement: HTMLElement,
        handleElement: HTMLElement,
        pointerId: number,
      ) => void;
      onKeyboardResize: (deltaPx: number, headerElement: HTMLElement) => void;
    }
  | {
      resizable?: false;
      resizeAriaLabel?: undefined;
      resizeValueNow?: undefined;
      resizeValueMin?: undefined;
      resizeValueMax?: undefined;
      onResizeStart?: undefined;
      onKeyboardResize?: undefined;
    };

interface DataTableHeaderCellProps {
  className?: string;
  children: ReactNode;
  /** aria-sort for sortable headers */
  ariaSort?: "ascending" | "descending" | "none";
  /** Native tooltip with the full label (headers truncate in narrow columns). */
  title?: string;
}

/** Shared th chrome + optional resize handle for sortable/static headers. */
export function DataTableHeaderCell({
  className,
  children,
  ariaSort,
  title,
  ...resize
}: DataTableHeaderCellProps & HeaderResizeProps) {
  const resizable = resize.resizable === true;

  return (
    <th
      className={cn(className, resizable && "relative overflow-hidden")}
      aria-sort={ariaSort}
      title={title}
    >
      {children}
      {resizable ? (
        <ColumnResizeHandle
          ariaLabel={resize.resizeAriaLabel}
          ariaValueNow={resize.resizeValueNow}
          ariaValueMin={resize.resizeValueMin}
          ariaValueMax={resize.resizeValueMax}
          onResizeStart={resize.onResizeStart}
          onKeyboardResize={resize.onKeyboardResize}
        />
      ) : null}
    </th>
  );
}

export type { HeaderResizeProps };
